import { validateStorageData, sanitizeStorageData, logSecurityEvent } from './security';

const STORAGE_KEY = 'fpra_migrants_guide';
const STORAGE_VERSION = '1.0.0';

const defaultState = {
  version: STORAGE_VERSION,
  documents: {},
  notes: {},
  wizards: {},
  preferences: {
    language: 'fr',
    showEmergencyButton: true,
    showQuickContact: true,
    darkMode: false
  },
  searchHistory: [],
  lastVisited: [],
  syncedAt: new Date().toISOString()
};

export const getStorageData = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) return defaultState;
    
    const parsed = JSON.parse(data);
    
    // Validate data structure
    if (!validateStorageData(parsed)) {
      logSecurityEvent('invalid_storage_structure');
      return defaultState;
    }
    
    // Migrate if version mismatch
    if (parsed.version !== STORAGE_VERSION) {
      return { ...defaultState, ...parsed, version: STORAGE_VERSION };
    }
    
    return parsed;
  } catch (error) {
    logSecurityEvent('storage_read_error', { error: error.message });
    console.error('Error reading localStorage:', error);
    return defaultState;
  }
};

export const setStorageData = (data) => {
  try {
    // Sanitize data before saving
    const sanitized = sanitizeStorageData(data);
    
    if (!sanitized) {
      logSecurityEvent('invalid_data_rejected');
      return false;
    }
    
    // Check if data actually changed
    const currentData = localStorage.getItem(STORAGE_KEY);
    if (currentData) {
      const parsed = JSON.parse(currentData);
      // Compare without syncedAt to avoid unnecessary updates
      const { syncedAt: oldSync, ...oldData } = parsed;
      const { syncedAt: newSync, ...newData } = sanitized;
      
      if (JSON.stringify(oldData) === JSON.stringify(newData)) {
        return true; // No changes, skip update
      }
    }
    
    const updated = {
      ...sanitized,
      syncedAt: new Date().toISOString()
    };
    
    // Check storage size (prevent quota exceeded)
    const dataSize = JSON.stringify(updated).length;
    if (dataSize > 5 * 1024 * 1024) { // 5MB limit
      logSecurityEvent('storage_quota_warning', { size: dataSize });
      return false;
    }
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    return true;
  } catch (error) {
    logSecurityEvent('storage_write_error', { error: error.message });
    console.error('Error writing to localStorage:', error);
    return false;
  }
};

export const updateDocuments = (documents) => {
  const data = getStorageData();
  data.documents = documents;
  return setStorageData(data);
};

export const updateNotes = (notes) => {
  const data = getStorageData();
  data.notes = notes;
  return setStorageData(data);
};

export const updateWizards = (wizards) => {
  const data = getStorageData();
  data.wizards = wizards;
  return setStorageData(data);
};

export const updatePreferences = (preferences) => {
  const data = getStorageData();
  data.preferences = { ...data.preferences, ...preferences };
  return setStorageData(data);
};

export const addSearchHistory = (query) => {
  const data = getStorageData();
  data.searchHistory = [
    { query, timestamp: new Date().toISOString() },
    ...data.searchHistory.filter(item => item.query !== query).slice(0, 9)
  ];
  return setStorageData(data);
};

export const addLastVisited = (pageId, pageTitle) => {
  const data = getStorageData();
  data.lastVisited = [
    { pageId, pageTitle, timestamp: new Date().toISOString() },
    ...data.lastVisited.filter(item => item.pageId !== pageId).slice(0, 9)
  ];
  return setStorageData(data);
};

export const clearAllData = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    return true;
  } catch (error) {
    console.error('Error clearing localStorage:', error);
    return false;
  }
};

export const exportData = () => {
  const data = getStorageData();
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `fpra-guide-backup-${new Date().toISOString().split('T')[0]}.json`;
  a.click();
  URL.revokeObjectURL(url);
};

export const importData = (jsonString) => {
  try {
    const data = JSON.parse(jsonString);
    return setStorageData(data);
  } catch (error) {
    console.error('Error importing data:', error);
    return false;
  }
};
